import { createRoute } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { useCallback } from "react"

import { CatalogScreen } from "../../screens/directories/catalog/catalog-screen"
import { PricesScreen } from "../../screens/directories/catalog/prices-screen"
import { StepsScreen } from "../../screens/directories/catalog/steps-screen"
import { ClientCardScreen } from "../../screens/directories/clients/client-card-screen"
import { ClientsScreen } from "../../screens/directories/clients/clients-screen"
import { DuplicatesScreen } from "../../screens/directories/clients/duplicates-screen"
import { MergeScreen } from "../../screens/directories/clients/merge-screen"
import type { ClientsSearch } from "../../screens/directories/clients/clients-search"
import { parseClientsSearch } from "../../screens/directories/clients/clients-search"
import { ChecklistScreen } from "../../screens/directories/onboarding/checklist-screen"
import { PageReadyScreen } from "../../screens/directories/onboarding/page-ready-screen"
import { EmployeeProfileScreen } from "../../screens/directories/staff/employee-profile-screen"
import { EmployeeLeaveScreen } from "../../screens/directories/staff/leave-screen"
import { StaffScreen } from "../../screens/directories/staff/staff-screen"
import { CABINET_PATHS } from "../../shell/nav-items"
import { protectedRoute } from "./protected-route"
import { SectionGuard } from "./section-guard"

/**
 * Адреса справочников и первого запуска.
 *
 * ПОЧЕМУ ОТДЕЛЬНЫЙ ФАЙЛ, А НЕ СТРОКИ В section-routes.tsx. Над кабинетом работают
 * несколько агентов одновременно, и общий файл маршрутов — единственное место, где они
 * неизбежно сталкиваются. Здесь свои маршруты объявлены целиком, а в общем файле
 * остаётся одна строка подключения.
 *
 * У КАЖДОГО ЗНАЧИМОГО ПОД-ЭКРАНА СВОЙ АДРЕС (ТЗ §4), и это не формальность:
 *
 * - /clients?q=…&stale=… — выборка для обзвона, её пересылают в чате;
 * - /clients/$clientId — карточку открывают по ссылке из журнала;
 * - /catalog/$serviceId — «посмотри, тут цена не та»;
 * - /staff?week=… и /staff/schedule?week=… — «поставь смену на ту неделю»;
 * - /staff/$employeeId/leave — сценарий на четыре шага, который не должен теряться
 *   при обновлении страницы вместе со всем набранным;
 * - /start и /start/page — первый запуск: к чек-листу возвращаются много раз за вечер.
 */

interface WeekSearch {
  readonly week?: string | undefined
}

/** Неделя в адресе — календарная дата. Чужое значение игнорируется, а не ломает экран. */
function parseWeekSearch(search: Record<string, unknown>): WeekSearch {
  const week = search["week"]
  return {
    week: typeof week === "string" && /^\d{4}-\d{2}-\d{2}$/.test(week) ? week : undefined,
  }
}

export const clientsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: CABINET_PATHS.clients,
  validateSearch: parseClientsSearch,
  component: function ClientsRouteComponent(): ReactNode {
    const search = clientsRoute.useSearch()
    const navigate = clientsRoute.useNavigate()

    const onSearchChange = useCallback(
      (patch: ClientsSearch) => {
        void navigate({ search: (previous) => ({ ...previous, ...patch }) })
      },
      [navigate],
    )

    const onOpenClient = useCallback(
      (clientId: string) => {
        void navigate({ to: "/clients/$clientId", params: { clientId } })
      },
      [navigate],
    )

    const onOpenJournal = useCallback(() => {
      void navigate({ to: CABINET_PATHS.journal, search: {} })
    }, [navigate])

    return (
      /*
       * Раздел защищён правом clients.list.view так же, как остальные, но отказ
       * внутри списка — СВОЙ: мастеру, пришедшему по ссылке, показывается не общее
       * «раздел вам не открыт», а «ваши клиенты — в вашем расписании» с кнопкой туда.
       * Поэтому страж здесь не ставится, а право проверяет сам экран.
       */
      <ClientsScreen
        search={search}
        onSearchChange={onSearchChange}
        onOpenClient={onOpenClient}
        onOpenJournal={onOpenJournal}
        onOpenDuplicates={() => {
          void navigate({ to: "/clients/duplicates" })
        }}
      />
    )
  },
})

export const clientCardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/clients/$clientId",
  component: function ClientCardRouteComponent(): ReactNode {
    const { clientId } = clientCardRoute.useParams()
    const navigate = clientCardRoute.useNavigate()

    const onBack = useCallback(() => {
      void navigate({ to: CABINET_PATHS.clients, search: {} })
    }, [navigate])

    return (
      <SectionGuard id="clients">
        <ClientCardScreen
          clientId={clientId}
          onBack={onBack}
          onOpenClient={(id) => {
            void navigate({ to: "/clients/$clientId", params: { clientId: id } })
          }}
          onOpenDuplicates={() => {
            void navigate({ to: "/clients/duplicates" })
          }}
        />
      </SectionGuard>
    )
  },
})

/**
 * 3-А7 «Дубли» — свой адрес: после импорта «разбери дубли» присылают ссылкой.
 * Право clients.merge — то же, что требует сервер.
 */
export const clientDuplicatesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/clients/duplicates",
  component: function ClientDuplicatesRouteComponent(): ReactNode {
    const navigate = clientDuplicatesRoute.useNavigate()
    return (
      <SectionGuard id="clients" permission="clients.merge">
        <DuplicatesScreen
          onBack={() => {
            void navigate({ to: CABINET_PATHS.clients, search: {} })
          }}
          onOpenClient={(clientId) => {
            void navigate({ to: "/clients/$clientId", params: { clientId } })
          }}
          onCompare={(left, right) => {
            void navigate({ to: "/clients/merge", search: { left, right } })
          }}
        />
      </SectionGuard>
    )
  },
})

/** Сравнение и слияние пары: /clients/merge?left=…&right=… */
export const clientMergeRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/clients/merge",
  validateSearch: (raw: Record<string, unknown>): { left: string; right: string } => ({
    left: typeof raw["left"] === "string" ? raw["left"] : "",
    right: typeof raw["right"] === "string" ? raw["right"] : "",
  }),
  component: function ClientMergeRouteComponent(): ReactNode {
    const search = clientMergeRoute.useSearch()
    const navigate = clientMergeRoute.useNavigate()
    return (
      <SectionGuard id="clients" permission="clients.merge">
        <MergeScreen
          key={`${search.left}:${search.right}`}
          leftId={search.left}
          rightId={search.right}
          onBack={() => {
            void navigate({ to: "/clients/duplicates" })
          }}
          onOpenClient={(clientId) => {
            void navigate({ to: "/clients/$clientId", params: { clientId } })
          }}
        />
      </SectionGuard>
    )
  },
})

export const catalogRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: CABINET_PATHS.catalog,
  component: function CatalogRouteComponent(): ReactNode {
    const navigate = catalogRoute.useNavigate()

    const onSelectService = useCallback(
      (serviceId: string | null) => {
        if (serviceId === null) return
        void navigate({ to: "/catalog/$serviceId", params: { serviceId } })
      },
      [navigate],
    )

    return (
      <SectionGuard id="catalog">
        <CatalogScreen
          serviceId={null}
          onSelectService={onSelectService}
          onOpenSteps={(serviceId) => {
            void navigate({ to: "/catalog/$serviceId/steps", params: { serviceId } })
          }}
          onOpenPrices={() => {
            void navigate({ to: "/catalog/prices" })
          }}
        />
      </SectionGuard>
    )
  },
})

export const catalogServiceRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/catalog/$serviceId",
  component: function CatalogServiceRouteComponent(): ReactNode {
    const { serviceId } = catalogServiceRoute.useParams()
    const navigate = catalogServiceRoute.useNavigate()

    const onSelectService = useCallback(
      (next: string | null) => {
        if (next === null) return
        void navigate({ to: "/catalog/$serviceId", params: { serviceId: next } })
      },
      [navigate],
    )

    return (
      <SectionGuard id="catalog">
        <CatalogScreen
          serviceId={serviceId}
          onSelectService={onSelectService}
          onOpenSteps={(id) => {
            void navigate({ to: "/catalog/$serviceId/steps", params: { serviceId: id } })
          }}
          onOpenPrices={() => {
            void navigate({ to: "/catalog/prices" })
          }}
        />
      </SectionGuard>
    )
  },
})

/**
 * 3-Б10/Б11 «Цены по времени» — карта цен, проверка и правила. Объявлен до /catalog/$serviceId:
 * статический путь «prices» иначе читался бы как идентификатор услуги.
 */
export const catalogPricesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/catalog/prices",
  component: function CatalogPricesRouteComponent(): ReactNode {
    const navigate = catalogPricesRoute.useNavigate()
    return (
      <SectionGuard id="catalog">
        <PricesScreen
          onBack={() => {
            void navigate({ to: CABINET_PATHS.catalog })
          }}
        />
      </SectionGuard>
    )
  },
})

/** 3-Б3 «Составная услуга с паузой» — шаги и полоса времени, свой адрес. */
export const catalogStepsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/catalog/$serviceId/steps",
  component: function CatalogStepsRouteComponent(): ReactNode {
    const { serviceId } = catalogStepsRoute.useParams()
    const navigate = catalogStepsRoute.useNavigate()
    return (
      <SectionGuard id="catalog">
        <StepsScreen
          serviceId={serviceId}
          onBack={() => {
            void navigate({ to: "/catalog/$serviceId", params: { serviceId } })
          }}
        />
      </SectionGuard>
    )
  },
})

export const staffRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: CABINET_PATHS.staff,
  validateSearch: parseWeekSearch,
  component: function StaffRouteComponent(): ReactNode {
    const { week } = staffRoute.useSearch()
    const navigate = staffRoute.useNavigate()

    const onWeekChange = useCallback(
      (next: string) => {
        void navigate({ search: { week: next } })
      },
      [navigate],
    )

    const onEmployeeLeave = useCallback(
      (employeeId: string) => {
        void navigate({ to: "/staff/$employeeId/leave", params: { employeeId } })
      },
      [navigate],
    )

    const onEmployeeProfile = useCallback(
      (employeeId: string) => {
        void navigate({ to: "/staff/$employeeId/profile", params: { employeeId } })
      },
      [navigate],
    )

    return (
      <SectionGuard id="staff">
        <StaffScreen
          week={week ?? null}
          onWeekChange={onWeekChange}
          onEmployeeLeave={onEmployeeLeave}
          onEmployeeProfile={onEmployeeProfile}
        />
      </SectionGuard>
    )
  },
})

export const staffScheduleRoute = createRoute({
  getParentRoute: () => protectedRoute,
  // Статический сегмент объявлен ДО «/staff/$employeeId/leave» не по порядку в файле,
  // а по правилу маршрутизатора: точное совпадение сильнее параметра, поэтому
  // «schedule» не будет прочитан как идентификатор мастера.
  path: "/staff/schedule",
  validateSearch: parseWeekSearch,
  component: function StaffScheduleRouteComponent(): ReactNode {
    const { week } = staffScheduleRoute.useSearch()
    const navigate = staffScheduleRoute.useNavigate()

    const onWeekChange = useCallback(
      (next: string) => {
        void navigate({ search: { week: next } })
      },
      [navigate],
    )

    return (
      <SectionGuard id="staff">
        <StaffScreen
          scheduleOnly
          week={week ?? null}
          onWeekChange={onWeekChange}
          onEmployeeLeave={() => {
            // На экране «только график» строк сотрудников нет, и выводить некого.
          }}
        />
      </SectionGuard>
    )
  },
})

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

export const employeeLeaveRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/staff/$employeeId/leave",
  // Период из просьбы мастера (5-М6): ?from=…&to=… — форма открывается уже с ним.
  validateSearch: (raw: Record<string, unknown>): { from?: string; to?: string } => ({
    ...(typeof raw["from"] === "string" && ISO_DATE.test(raw["from"]) ? { from: raw["from"] } : {}),
    ...(typeof raw["to"] === "string" && ISO_DATE.test(raw["to"]) ? { to: raw["to"] } : {}),
  }),
  component: function EmployeeLeaveRouteComponent(): ReactNode {
    const { employeeId } = employeeLeaveRoute.useParams()
    const search = employeeLeaveRoute.useSearch()
    const navigate = employeeLeaveRoute.useNavigate()

    const onClose = useCallback(() => {
      void navigate({ to: CABINET_PATHS.staff, search: {} })
    }, [navigate])

    return (
      <SectionGuard id="staff">
        <EmployeeLeaveScreen
          employeeId={employeeId}
          onClose={onClose}
          requested={search.from !== undefined && search.to !== undefined ? { from: search.from, to: search.to } : null}
        />
      </SectionGuard>
    )
  },
})

/** 3-Б7 «Публичный профиль мастера» — своя страница: на телефоне предпросмотр отдельным экраном. */
export const employeeProfileRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/staff/$employeeId/profile",
  component: function EmployeeProfileRouteComponent(): ReactNode {
    const { employeeId } = employeeProfileRoute.useParams()
    const navigate = employeeProfileRoute.useNavigate()

    const onClose = useCallback(() => {
      void navigate({ to: CABINET_PATHS.staff, search: {} })
    }, [navigate])

    return (
      <SectionGuard id="staff">
        <EmployeeProfileScreen employeeId={employeeId} onClose={onClose} />
      </SectionGuard>
    )
  },
})

export const onboardingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/start",
  component: function OnboardingRouteComponent(): ReactNode {
    const navigate = onboardingRoute.useNavigate()

    const onOpen = useCallback(
      (path: string) => {
        void navigate({ to: path, search: {} })
      },
      [navigate],
    )

    const onOpenReady = useCallback(() => {
      void navigate({ to: "/start/page" })
    }, [navigate])

    return <ChecklistScreen onOpen={onOpen} onOpenReady={onOpenReady} />
  },
})

export const onboardingReadyRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/start/page",
  component: function OnboardingReadyRouteComponent(): ReactNode {
    const navigate = onboardingReadyRoute.useNavigate()

    const onOpenSchedule = useCallback(() => {
      void navigate({ to: "/staff/schedule", search: {} })
    }, [navigate])

    return <PageReadyScreen onOpenSchedule={onOpenSchedule} />
  },
})

/** Маршруты справочников и первого запуска, живущие внутри защищённой области. */
export const directoryRoutes = [
  clientsRoute,
  clientDuplicatesRoute,
  clientMergeRoute,
  clientCardRoute,
  catalogRoute,
  catalogPricesRoute,
  catalogServiceRoute,
  catalogStepsRoute,
  staffScheduleRoute,
  staffRoute,
  employeeLeaveRoute,
  employeeProfileRoute,
  onboardingRoute,
  onboardingReadyRoute,
]
